import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  QrCode,
  Smartphone,
  BellRing,
  ShieldCheck,
  ChevronRight,
  Wine,
  UtensilsCrossed,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../store';

export default function Home() {
  const activePortal = useAuthStore((state) => state.activePortal);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());

  const dashboardHref = activePortal === 'superadmin'
    ? '/superadmin/dashboard'
    : activePortal === 'admin'
      ? '/admin/dashboard'
      : '/staff/dashboard';

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-[#111827]">
      <header className="sticky top-0 z-40 border-b border-gray-200/80 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#7C3AED] shadow-sm">
              <Wine className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-lg font-bold leading-none" style={{ fontFamily: 'Manrope' }}>
                GlassOrder
              </p>
              <p className="text-xs text-gray-500">Commande QR pour établissements</p>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            {isAuthenticated && activePortal ? (
              <Link to={dashboardHref}>
                <Button className="rounded-full bg-[#111827] px-5 hover:bg-black">
                  Accéder au tableau de bord
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/staff/login">
                  <Button variant="outline" className="rounded-full border-gray-300">
                    Espace staff
                  </Button>
                </Link>
                <Link to="/admin/login">
                  <Button className="rounded-full bg-[#111827] px-5 hover:bg-black">
                    Espace admin
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(34,197,94,0.12),_transparent_35%),radial-gradient(circle_at_top_left,_rgba(17,24,39,0.06),_transparent_25%)]" />
          <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:py-24">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-violet-700">
                <Sparkles className="h-4 w-4" />
                Expérience fluide pour les clients et le personnel
              </div>

              <h1
                className="max-w-3xl text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl"
                style={{ fontFamily: 'Manrope' }}
              >
                Le menu et la commande QR pensés pour les bars, restos et plages privées.
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600">
                Les clients scannent, consultent le menu, commandent depuis leur téléphone
                et suivent leur commande en temps réel. Le staff gagne du temps, l’établissement
                fluidifie le service.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                {isAuthenticated ? (
                  <Link to={dashboardHref}>
                    <Button className="h-12 rounded-full bg-[#111827] px-6 text-base hover:bg-black">
                      Ouvrir mon espace
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/admin/login">
                      <Button className="h-12 rounded-full bg-[#111827] px-6 text-base hover:bg-black">
                        Démarrer côté établissement
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/staff/login">
                      <Button
                        variant="outline"
                        className="h-12 rounded-full border-gray-300 px-6 text-base"
                      >
                        Connexion staff
                      </Button>
                    </Link>
                  </>
                )}
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                  <p className="text-sm font-semibold text-gray-900">Scan immédiat</p>
                  <p className="mt-1 text-sm text-gray-500">QR de table, accès direct au menu.</p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                  <p className="text-sm font-semibold text-gray-900">Suivi live</p>
                  <p className="mt-1 text-sm text-gray-500">Statuts de commande visibles en temps réel.</p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                  <p className="text-sm font-semibold text-gray-900">Pilotage simple</p>
                  <p className="mt-1 text-sm text-gray-500">Menu, tables, staff et commandes centralisés.</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="relative"
            >
              <div className="rounded-[32px] border border-gray-200 bg-white p-5 shadow-2xl shadow-gray-200/70">
                <div className="rounded-[28px] bg-[#111827] p-5 text-white">
                  <div className="mb-5 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-400">Table 12</p>
                      <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                        Le Rooftop
                      </h2>
                    </div>
                    <div className="rounded-2xl bg-white/10 p-3">
                      <QrCode className="h-8 w-8" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="rounded-2xl bg-white/10 p-4">
                      <div className="flex items-center justify-between text-sm">
                        <span>Spritz Signature</span>
                        <span>12,00 €</span>
                      </div>
                    </div>
                    <div className="rounded-2xl bg-white/10 p-4">
                      <div className="flex items-center justify-between text-sm">
                        <span>Planche apéro</span>
                        <span>16,00 €</span>
                      </div>
                    </div>
                    <div className="rounded-2xl bg-blue-500/20 p-4 ring-1 ring-blue-400/30">
                      <div className="flex items-center justify-between text-sm font-medium text-blue-300">
                        <span>Commande en préparation</span>
                        <span>2 min</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl bg-gray-50 p-4">
                    <QrCode className="mb-3 h-5 w-5 text-[#111827]" />
                    <p className="text-sm font-semibold">Scan du QR</p>
                    <p className="mt-1 text-xs leading-5 text-gray-500">
                      Le client accède instantanément à sa table.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4">
                    <Smartphone className="mb-3 h-5 w-5 text-[#111827]" />
                    <p className="text-sm font-semibold">Commande mobile</p>
                    <p className="mt-1 text-xs leading-5 text-gray-500">
                      Parcours simple, rapide, sans attente inutile.
                    </p>
                  </div>
                  <div className="rounded-2xl bg-gray-50 p-4">
                    <BellRing className="mb-3 h-5 w-5 text-[#111827]" />
                    <p className="text-sm font-semibold">Notifications staff</p>
                    <p className="mt-1 text-xs leading-5 text-gray-500">
                      Les équipes voient les commandes dès leur arrivée.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-10 lg:py-16">
          <div className="mb-8">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet-600">
              Comment ça marche
            </p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Un parcours simple, du scan à la livraison
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100">
                <QrCode className="h-6 w-6 text-violet-700" />
              </div>
              <h3 className="text-lg font-semibold">1. Le client scanne</h3>
              <p className="mt-2 text-sm leading-7 text-gray-600">
                Le QR code de table ouvre immédiatement l’expérience client sur mobile.
              </p>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100">
                <UtensilsCrossed className="h-6 w-6 text-blue-700" />
              </div>
              <h3 className="text-lg font-semibold">2. Il commande</h3>
              <p className="mt-2 text-sm leading-7 text-gray-600">
                Menu clair, options, panier, validation de commande et suivi en temps réel.
              </p>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100">
                <ShieldCheck className="h-6 w-6 text-amber-700" />
              </div>
              <h3 className="text-lg font-semibold">3. Le staff traite</h3>
              <p className="mt-2 text-sm leading-7 text-gray-600">
                L’équipe voit les commandes, met à jour leur statut et fluidifie le service.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 pb-16 lg:pb-24">
          <div className="rounded-[32px] bg-[#111827] px-8 py-10 text-white lg:px-12 lg:py-14">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-400">
                  Accès établissement
                </p>
                <h2 className="mt-2 text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
                  Connectez-vous à votre espace GlassOrder
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-gray-300">
                  Administration, menu, tables, staff, analytics et suivi opérationnel depuis une seule interface.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Link to="/staff/login">
                  <Button
                    variant="outline"
                    className="h-12 rounded-full border-white/20 bg-transparent px-6 text-white hover:bg-white/10"
                  >
                    Connexion staff
                  </Button>
                </Link>
                <Link to="/admin/login">
                  <Button className="h-12 rounded-full bg-[#7C3AED] px-6 text-white hover:bg-violet-600">
                    Connexion admin
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
